import { AlertTriangle, Edit, Plus, Shield, Users } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { CardSkeleton } from "@/components/common/LoadingSkeleton";
import { EmptyState } from "@/components/common/EmptyState";
import { toast } from "@/components/ui/Toast";
import { api } from "@/lib/api-client";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// RolesManagement — Screen 13 (AxkxA)
//
// Role cards grid with System/Custom badges.
// Design: §8.5 Screen 13
// ─────────────────────────────────────────────

// ── Types ─────────────────────────────────────

interface PolicyRule {
  action: string;
  subject: string;
}

interface Policy {
  id: string;
  roleId: string;
  domainId: string | null;
  rules: PolicyRule[];
}

interface Role {
  id: string;
  name: string;
  description?: string;
  isSystem: boolean;
  userCount?: number;
  policies?: Policy[];
  createdAt: string;
}

// ── Role card ─────────────────────────────────

function RoleCard({
  role,
  onEdit,
}: {
  role: Role;
  onEdit: (role: Role) => void;
}) {
  const { t } = useTranslation();
  // Summarize policies into readable lines. Dedupe across all policies first,
  // then slice for display so the "+N more" count reflects hidden permission
  // lines (not the raw policy count, which is unrelated).
  const allPerms = role.policies
    ? [...new Set(
        role.policies.flatMap((p) =>
          (p.rules || []).map((r) => `${r.action} ${r.subject}`)
        )
      )]
    : [];
  const permSummary = allPerms.slice(0, 4);

  return (
    <div className="flex flex-col gap-4 rounded-card border border-border-default bg-card p-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <h3 className="text-[16px] font-medium text-text-primary">
              {role.name}
            </h3>
            {role.isSystem ? (
              <span className="inline-flex items-center rounded-chip bg-accent-bg px-1.5 py-0.5 text-[11px] font-semibold text-accent-text">
                {t("roles.system")}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-chip bg-hover px-1.5 py-0.5 text-[11px] font-semibold text-text-muted">
                {t("roles.custom")}
              </span>
            )}
          </div>
          {role.description && (
            <p className="line-clamp-2 text-body text-text-secondary">
              {role.description}
            </p>
          )}
        </div>
        <button
          type="button"
          title="Edit role"
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-control text-text-muted transition-colors hover:bg-hover hover:text-text-secondary"
          onClick={() => onEdit(role)}
        >
          <Edit className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Separator */}
      <div className="h-px bg-border-subtle" />

      {/* Stats row */}
      <div className="flex items-center gap-2">
        <Users className="h-3.5 w-3.5 text-text-muted" />
        <span className="text-caption text-text-muted">
          {role.userCount ?? 0} {t("roles.userCount")}
        </span>
      </div>

      {/* Permission summary */}
      {permSummary.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-[11.5px] font-medium uppercase tracking-[1.5px] text-text-muted">
            {t("roles.permissions")}
          </span>
          <div className="flex flex-col gap-1">
            {permSummary.map((line, idx) => (
              <span key={idx} className="text-caption text-text-secondary">
                {line}
              </span>
            ))}
            {allPerms.length > permSummary.length && (
              <span className="text-caption text-text-muted">
                +{allPerms.length - permSummary.length} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────

export function RolesManagement() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get<{ data: Role[] }>("/roles");
      const mapped = (res.data ?? []).map((r: any) => ({
        ...r,
        userCount: r._count?.userProfiles ?? 0,
      }));
      setRoles(mapped);
      setIsError(false);
    } catch {
      // Distinguish a real failure from a genuinely empty result so the UI can
      // show an error/retry state instead of the "no roles created" empty state.
      setRoles([]);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  function handleEdit(role: Role) {
    if (role.isSystem) {
      toast.info(t("roles.systemRoleTitle"), t("roles.systemRoleInfo"));
      return;
    }
    navigate(`/roles/${role.id}/edit`);
  }

  return (
    <>
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-[24px] font-semibold text-text-primary">
            {t("roles.title")}
          </h1>
          <p className="text-body text-text-secondary">
            {t("roles.subtitle")}
          </p>
        </div>
        <Button
          leadingIcon={<Plus className="h-4 w-4" />}
          onClick={() => navigate("/roles/new")}
        >
          {t("roles.newRole")}
        </Button>
      </div>

      {/* Role cards grid — distinguish a load error from a genuinely empty list */}
      {isLoading ? (
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-card border border-border-default bg-card">
          <EmptyState
            icon={AlertTriangle}
            title="Couldn't load roles"
            description={t("errors.generic")}
            action={{ label: t("common.refresh"), onClick: () => { fetchRoles(); } }}
          />
        </div>
      ) : roles.length === 0 ? (
        <EmptyState
          icon={Shield}
          title={t("roles.noRolesCreated")}
          description={t("roles.noRolesCreatedDesc")}
          action={{
            label: `+ ${t("roles.newRole")}`,
            onClick: () => navigate("/roles/new"),
          }}
        />
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {roles.map((role) => (
            <RoleCard key={role.id} role={role} onEdit={handleEdit} />
          ))}
        </div>
      )}
    </>
  );
}
